import { ChevronLeft, ChevronRight } from 'lucide-react';
import TopNav from './TopNav';
import QuestionArea from './QuestionArea';
import QuestionNavigator from './QuestionNavigator';
import { demoQuestions, QTYPES } from '../../data/demoQuestions';
import { useDemoTheme } from './DemoThemeContext';

const DIFF_COLORS = {
  Easy:   { bg: 'rgba(34,197,94,0.1)',   text: '#4ade80',  border: 'rgba(34,197,94,0.25)' },
  Medium: { bg: 'rgba(245,158,11,0.1)',  text: '#fbbf24',  border: 'rgba(245,158,11,0.25)' },
  Hard:   { bg: 'rgba(239,68,68,0.1)',   text: '#f87171',  border: 'rgba(239,68,68,0.25)' },
  'N/A':  { bg: 'var(--d-hover)',         text: 'var(--d-text-3)', border: 'var(--d-border)' },
};

const TYPE_LABELS = {
  [QTYPES.MCQ]:    'Multiple Choice',
  [QTYPES.CODING]: 'Coding Challenge',
  [QTYPES.GRID]:   'Fill in the Blanks',
  [QTYPES.FILE]:   'File Submission',
};

export default function ExamLayout({ currentQuestion, answers, questionStatus, timeLeft, isFullscreen, showAutoSave, onNavigate, onAnswer, onSubmit, onToggleFullscreen, onToggleReview }) {
  const { isDark } = useDemoTheme();
  const question = demoQuestions[currentQuestion];
  const diff = DIFF_COLORS[question?.difficulty] || DIFF_COLORS['N/A'];

  const handlePrev = () => { if (currentQuestion > 0) onNavigate(currentQuestion - 1); };
  const handleNext = () => { if (currentQuestion < demoQuestions.length - 1) onNavigate(currentQuestion + 1); };

  return (
    <div className="flex flex-col h-screen" style={{ background: 'var(--d-bg)', color: 'var(--d-text-hi)' }} data-testid="exam-layout">
      <TopNav
        currentQuestion={currentQuestion} timeLeft={timeLeft} isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen} onSubmit={onSubmit}
        onToggleReview={onToggleReview} questionStatus={questionStatus} showAutoSave={showAutoSave}
      />

      <div className="flex flex-1 min-h-0">
        {/* Left: Question Panel */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
          {/* Question header */}
          <div className="flex-shrink-0 px-8 pt-6 pb-4" style={{ borderBottom: '1px solid var(--d-border-sub)' }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs font-mono px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(124,58,237,0.1)', color: isDark ? '#a78bfa' : '#6d28d9', border: '1px solid rgba(124,58,237,0.2)' }}>
                    {question?.category}
                  </span>
                  <span className="text-xs font-mono px-2.5 py-1 rounded-lg"
                    style={{ background: diff.bg, color: diff.text, border: `1px solid ${diff.border}` }}>
                    {question?.difficulty}
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-lg"
                    style={{ background: 'var(--d-hover)', color: 'var(--d-text-3)', border: '1px solid var(--d-border)' }}>
                    {TYPE_LABELS[question?.type]}
                  </span>
                </div>
                <h2 className="text-base font-semibold leading-snug" style={{ color: 'var(--d-text-hi)' }}>
                  Q{question?.id}. {question?.title}
                </h2>
              </div>
              <span className="text-xs font-mono flex-shrink-0 mt-1"
                style={{ color: 'var(--d-text-3)', background: 'var(--d-hover)', border: '1px solid var(--d-border)', padding: '4px 10px', borderRadius: 8 }}>
                {question?.points} pts
              </span>
            </div>
          </div>

          {/* Question content */}
          <div className="flex-1 overflow-y-auto">
            <QuestionArea question={question} answer={answers[question?.id]} onAnswer={onAnswer} />
          </div>

          {/* Footer nav */}
          <div className="flex-shrink-0 flex items-center justify-between px-8 py-4" style={{ borderTop: '1px solid var(--d-border-sub)' }}>
            <button data-testid="prev-question-btn" onClick={handlePrev} disabled={currentQuestion === 0}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ background: 'var(--d-hover)', border: '1px solid var(--d-border)', color: 'var(--d-text-2)' }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.color = 'var(--d-text-hi)'; }}
              onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.color = 'var(--d-text-2)'; }}>
              <ChevronLeft size={15} />Previous
            </button>

            <span className="text-xs font-mono" style={{ color: 'var(--d-text-3)' }}>Alt+P / Alt+N</span>

            {currentQuestion < demoQuestions.length - 1 ? (
              <button data-testid="next-question-btn" onClick={handleNext}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)', color: isDark ? '#a78bfa' : '#6d28d9' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(124,58,237,0.18)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(124,58,237,0.1)')}>
                Next<ChevronRight size={15} />
              </button>
            ) : (
              <button data-testid="final-submit-btn" onClick={onSubmit}
                className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}>
                Submit Assessment<ChevronRight size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Right: Navigator */}
        <div className="w-64 xl:w-72 flex-shrink-0 hidden md:flex flex-col overflow-y-auto" style={{ background: 'var(--d-bg-dim)', borderLeft: '1px solid var(--d-border-sub)' }}>
          <QuestionNavigator questions={demoQuestions} currentQuestion={currentQuestion} questionStatus={questionStatus} onNavigate={onNavigate} onSubmit={onSubmit} />
        </div>
      </div>
    </div>
  );
}
